import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { intlModule } from '@chedri/base';

import Avatar from 'components/Avatar';
import Icon from 'components/Icon';
import IconLink from 'components/IconLink';

import { getChatterUserAvatarInfos } from 'helpers/clients';
import Loader from 'components/layout/Loader';

function CommunicationUserInfo({
  user,
  isEmployee,
  isAgent,
  isOnline,
  alternativeText,
  onAgentChange,
  onShowSettings,
  onEndChat,
  isEndChatPending,
  isChatEnded,
}) {
  const {
    first_name: firstName = 'N',
    last_name: lastName = 'N',
    avatarThumbLink: userThumbLink,
    is_anonymous_user: isAnonymous,
    role,
    avatar,
    name,
  } = user || {};
  const { avatarThumbLink, avatarTextColor, avatarColor } = getChatterUserAvatarInfos({
    name: name || firstName + lastName,
    avatar: typeof avatar === 'string' ? avatar : userThumbLink,
  });
  const isCustomer = role === 'customer';
  const messages = useSelector(intlModule.selectors.getMessages);

  const changeAgent = useCallback(
    e => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      if (typeof onAgentChange === 'function') {
        onAgentChange(!isAgent);
      }
    },
    [isAgent]
  );

  const showSettings = useCallback(
    e => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      if (typeof onShowSettings === 'function') {
        onShowSettings();
      }
    },
    [isAgent]
  );

  const endChat = useCallback(
    e => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      if (typeof onEndChat === 'function') {
        onEndChat();
      }
    },
    [isAgent, onEndChat]
  );

  return (
    <div className="lh-47">
      <div className="pull-left p-r-10" style={{ marginTop: '4px' }}>
        <Avatar
          firstName={firstName}
          lastName={lastName}
          size={40}
          textColor={avatarTextColor}
          color={avatarColor}
          thumbLink={avatarThumbLink}
        />
      </div>
      <div className="pull-left">
        {alternativeText || <span className=" text-info bold">{`${firstName} ${lastName}`}</span>}
      </div>
      {isOnline != null && (
        <div className="pull-left">
          <Icon
            name="fa-circle"
            solid={isOnline}
            iconClassName={`${isOnline ? 'text-success ' : 'text-danger '}fa-fw fs-10 m-l-15`}
            tooltip={isOnline ? messages.user_is_online : messages.user_is_offline}
            tooltipPlacement="top"
          />
        </div>
      )}
      {isEmployee && (
        <div className="pull-right">
          <IconLink
            href="#activateAgent"
            name="fa-user-headset"
            duotone
            iconClassName={`${isAgent ? 'text-success ' : ''}m-r-15`}
            iconStyles={{ transform: 'scale(1.4)' }}
            linkClassName="text-master-light"
            tooltip={isAgent ? messages.deactivateAgent : messages.activateAgent}
            tooltipPlacement="top"
            onClick={changeAgent}
          />
          <IconLink
            href="#showSettings"
            name="fa-sliders-h-square"
            solid
            iconStyles={{ transform: 'scale(1.4)' }}
            linkClassName="text-master-light"
            onClick={showSettings}
          />
        </div>
      )}
      {(isAnonymous || isCustomer) && !isChatEnded && (
        <div className="pull-right">
          {isEndChatPending ? (
            <Loader small />
          ) : (
            <IconLink
              href="#endChat"
              name="fa-stop-circle"
              solid
              iconStyles={{ transform: 'scale(1.4)' }}
              linkClassName="text-master-light"
              label={messages.end_chat}
              onClick={endChat}
            />
          )}
        </div>
      )}
    </div>
  );
}
CommunicationUserInfo.defaultProps = {
  isEmployee: false,
  isAgent: false,
  isOnline: undefined,
  alternativeText: undefined,
  onAgentChange: () => undefined,
  onShowSettings: () => undefined,
};
CommunicationUserInfo.propTypes = {
  user: PropTypes.object.isRequired,
  isEmployee: PropTypes.bool,
  isAgent: PropTypes.bool,
  isOnline: PropTypes.bool,
  alternativeText: PropTypes.string,
  onAgentChange: PropTypes.func,
  onShowSettings: PropTypes.func,
};

export default CommunicationUserInfo;
